import LivePolling from '@live/pollingHandler';
import type { MediaAssetServiceResponse } from '@shared-types/mediaAssets.types';
import { MediaAssetServiceStatus } from '@shared-types/mediaAssets.types';
import { IS_SIMULATION } from '@utilities/browser';
import { mergeArrays, isEqual, splitArray } from '@utilities/collection-utils';
import { formatUrl } from '@utilities/fetch';
import { Warn } from '@utilities/logging';
import { MemoryCache } from '@utilities/memory-cache';
import { debounce } from '@utilities/throttle';

export type MediaAssetServiceCallback = (videos: MediaAssetServiceResponse[]) => unknown;
export type MediaAssetServiceListener = {
  videoIds: string[];
  callback?: MediaAssetServiceCallback;
  context?: any;
};

const arrayMaxSize = 20;
export class MediaAssetService {
  interval = 30000;
  baseUrl = window.masApiUrl;
  apiKey = window.apiKey;

  get isActive() {
    return !!this.baseUrl && !IS_SIMULATION;
  }

  listeners: MediaAssetServiceListener[] = [];
  savedUrls: { ids: string[]; url: string }[] = [];
  videosCache = new MemoryCache<string, MediaAssetServiceResponse>();

  get videoIds() {
    return mergeArrays(...this.listeners.map((l) => l.videoIds)).toSorted((a, b) => a.localeCompare(b));
  }

  /**
   *
   * @param videoIds Video Ids list
   * @param fn callback to be calld when a video status changes (arguments videos with di in video id list)
   * @param context the context of the caller
   */
  on(videoIds: (number | string)[], fn: MediaAssetServiceCallback, context: any = this) {
    if (!this.isActive) {
      const fakeResp = videoIds.map((id) => ({
        mediaAssetId: `${id}`,
        status: MediaAssetServiceStatus.AVAILABLE,
        hlsStreamUrl: IS_SIMULATION ? '//content.jwplatform.com/manifests/4vvgI762.m3u8' : undefined,
      }));
      fn && fn.call(context, fakeResp);
      return;
    }

    const stringVideoIds = this._toStringArray(videoIds).toSorted((a, b) => a.localeCompare(b));

    const newListener = {
      videoIds: stringVideoIds,
      callback: fn,
      context,
    };

    if (
      !this.listeners.some(
        (l) =>
          isEqual(l.videoIds, stringVideoIds) &&
          l.callback.toString() === fn.toString() &&
          context &&
          Object.is(l.context, context),
      )
    ) {
      this.listeners.push(newListener);
    }

    const cachedVideos = this._getResponse(stringVideoIds);
    if (cachedVideos.length === stringVideoIds.length) {
      fn.call(context, cachedVideos);
    }

    this._attach();
  }

  _attach = debounce(
    () => {
      const newUrls = this.getUrlsFromIds();
      if (isEqual(this.savedUrls, newUrls)) {
        return;
      }

      // if the url is changed (new videoId) change the polling cnnection
      this.savedUrls.forEach(({ ids, url }) => LivePolling.off(url, this._dispatch(ids), this));

      this.savedUrls = newUrls;

      this.savedUrls.forEach(({ ids, url }) => {
        LivePolling.on(url, [{ key: 'x-api-key', value: this.apiKey }], this.interval, this._dispatch(ids), this);
      });
    },
    250,
    false,
    this,
  );

  /**
   *
   * @param videoIds Video Ids list
   * @param callback callback to be calld when a video status changes
   * @param context the context of the caller
   */
  off(videoIds: (number | string)[], callback: MediaAssetServiceCallback, context?: any) {
    const stringVideoIds = this._toStringArray(videoIds);

    this.listeners = this.listeners.filter(
      (l) =>
        !isEqual(l.videoIds, stringVideoIds) ||
        (callback && l.callback.toString() !== callback.toString()) ||
        (context && !Object.is(l.context, context)),
    );

    this._attach();
  }

  getUrlsFromIds = () =>
    splitArray(this.videoIds, arrayMaxSize).map((ids) => ({
      ids,
      url: formatUrl(this.baseUrl, 'v1/access-rights', { mediaAssetIds: ids }),
    }));

  _dispatch = (ids: string[]) => (data: MediaAssetServiceResponse[]) => {
    const fullData = ids.map(
      (id) =>
        data?.find((v) => v.mediaAssetId === id) ??
        this.videosCache.get(id) ?? {
          mediaAssetId: id,
          status: MediaAssetServiceStatus.RESTRICTED,
        },
    );

    this._updateCache(fullData);

    this.listeners.forEach((l) => {
      const videos = this._getResponse(l.videoIds);
      if (videos.length < l.videoIds.length) return;

      try {
        l.callback.call(l.context, videos);
      } catch (e) {
        Warn(e, l, videos);
      }
    });
  };

  _updateCache = (data: MediaAssetServiceResponse[]) => {
    data.forEach((video) => {
      this.videosCache.set(video.mediaAssetId, video);
    });
  };

  _getResponse = (videoIds: string[]) => videoIds.map((id) => this.videosCache.get(id)).filter(Boolean);

  _toStringArray(videoIds: (number | string)[] = []) {
    if (!Array.isArray(videoIds)) throw new Error('videoIds MUST be an Array!!');
    return videoIds?.filter(Boolean).map((id) => `${id}`) ?? [];
  }

  static readonly instance = new MediaAssetService();
}

const mediaAssetServiceInstance = MediaAssetService.instance;
export default mediaAssetServiceInstance;
